import { useEffect, useRef, useState } from 'react';
import store from 'app-store';

/**
 * Bridge hook for reading redux state from function components while we migrate
 * away from flux/connect. react-redux is pinned at v4 in this app, which predates
 * the `useSelector`/`useDispatch` hooks, so we subscribe to the store manually.
 *
 * @param {(state: object) => any} selector - picks the slice of state you care about.
 * @returns the selected value, re-rendering when (and only when) it changes.
 */
export default function useReduxSelector(selector) {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const [selected, setSelected] = useState(() => selectorRef.current(store.getState()));

  useEffect(() => {
    const checkForUpdates = () => {
      const next = selectorRef.current(store.getState());
      // Bail out of re-renders when the selected slice is referentially equal.
      setSelected((prev) => (prev === next ? prev : next));
    };

    // Run once immediately in case the store changed between render and subscribe.
    checkForUpdates();

    const unsubscribe = store.subscribe(checkForUpdates);
    return unsubscribe;
  }, []);

  return selected;
}
